import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Bell, CheckCheck } from 'lucide-react';
import { log } from '@/lib/logger';
import NotificationItem from '@/components/notifications/NotificationItem';

const POLL_MS = 30000;

export default function NotificationBell() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=20`);
      setNotifications(response.data);
    } catch (error) { log.error('NotificationBell: fetchNotifications failed', error); }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.unread_count);
    } catch (error) { log.error('NotificationBell: fetchUnreadCount failed', error); }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchNotifications();
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_MS);
    return () => clearInterval(interval);
  }, [user, fetchNotifications, fetchUnreadCount]);

  const markRead = useCallback(async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {});
    } catch (error) { log.error('NotificationBell: markRead failed', error); }
  }, []);

  const handleClick = useCallback(async (notif) => {
    if (!notif.read) await markRead(notif.id);
    setOpen(false);
    if (notif.link) navigate(notif.link);
    setTimeout(() => { fetchNotifications(); fetchUnreadCount(); }, 100);
  }, [markRead, navigate, fetchNotifications, fetchUnreadCount]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {});
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) { log.error('NotificationBell: markAllRead failed', error); }
  }, [fetchNotifications, fetchUnreadCount]);

  const handleOpenChange = useCallback((isOpen) => {
    setOpen(isOpen);
    if (isOpen) fetchNotifications();
  }, [fetchNotifications]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button data-testid="notification-bell-btn" variant="ghost" size="icon" className="relative rounded-full">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent text-accent-foreground text-xs font-bold flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-medium">Notifications</h3>
          {unreadCount > 0 && (
            <Button data-testid="mark-all-read-btn" onClick={handleMarkAllRead} variant="ghost" size="sm" className="text-xs">
              <CheckCheck className="w-3 h-3 mr-1" />Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              <Bell className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
              No notifications yet
            </div>
          ) : (
            notifications.map((notif, idx) => (
              <NotificationItem key={notif.id} notif={notif} idx={idx} onClick={handleClick} />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
